import { showToast } from '@/features/toasts/toastsSlice';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useState } from 'react';
import { EditableTitleEdit } from './EditableTitleEdit';
import { EditableTitleView } from './EditableTitleView';

type EditableTitleProps<T> = {
  item?: T;
  title: string;
  onUpdate: (item: T, newTitle: string) => Promise<void>;
  isLoading?: boolean;
  isEditable?: boolean;
  className?: string;
  successMessage?: {
    title: string;
    message: string;
  };
  errorMessage?: {
    title: string;
    message: string;
  };
};

export function EditableTitle<T>({
  item,
  title,
  onUpdate,
  isLoading = false,
  isEditable = true,
  className = 'text-2xl font-semibold leading-8',
  successMessage,
  errorMessage,
}: EditableTitleProps<T>) {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    if (item && isEditable) {
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSave = async (newTitle: string) => {
    if (!item) return;

    try {
      await onUpdate(item, newTitle);
      setIsEditing(false);

      if (successMessage) {
        dispatch(
          showToast({
            type: ToastType.SUCCESS,
            title: successMessage.title,
            message: successMessage.message,
          })
        );
      }
    } catch (error) {
      if (errorMessage) {
        dispatch(
          showToast({
            type: ToastType.ERROR,
            title: errorMessage.title,
            message: errorMessage.message,
          })
        );
      }
    }
  };

  if (isEditing) {
    return (
      <EditableTitleEdit
        initialValue={title}
        onSave={handleSave}
        onCancel={handleCancelEdit}
        isLoading={isLoading}
        className={className}
      />
    );
  }

  return (
    <EditableTitleView
      title={title}
      isEditable={isEditable}
      className={className}
      onEditClick={handleEditClick}
    />
  );
}
